import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useReducer
} from 'react';
import { notification } from 'antd';
import { get as path } from 'lodash-es';
import * as Sentry from '@sentry/react';
import history from 'utils/history';

import {
  InvalidLoginOrPassword,
  PasswordChangeRequired,
  InvalidConfirmationCode,
  UserIsBlocked,
  UserError,
  NewPasswordRequired
} from 'errors/userErrors';
import { LimitReached } from 'errors/baseErrors';
import { getAxios, SERVICES, post, get } from 'utils/http';
import Spinner from 'components/ui/Spinner';
import { metrika } from 'utils/metrika';
import { User } from 'types';
import { api } from 'services';
import { store } from 'store/redux';
import { AxiosRequestConfig } from 'axios';
import { t as _t } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import {
  isDemoAccess,
  startDemoAccess,
  stopDemoAccess
} from 'features/demoAccess';
import { sessionReducer, setAuthHeader } from './sessionReducer';
import { LoginData, userContext } from './context';

export const handleError = (e: unknown, message?: string) => {
  let error: Error | undefined;
  if (e instanceof UserError) {
    error = e;
  }
  const errorCode = path(e, 'response.data.Error') as unknown;
  switch (errorCode) {
    case 'User does not exist.':
    case 'Incorrect username or password.':
      error = new InvalidLoginOrPassword();
      break;
    case 'User is disabled.':
      error = new UserIsBlocked();
      break;
    case 'Attempt limit exceeded, please try after some time.':
      error = new LimitReached();
      break;
    case 'CodeMismatchException':
      error = new InvalidConfirmationCode();
      break;
    case 'Password reset required for the user':
      error = new PasswordChangeRequired();
      break;
    case 'ExpiredCodeException':
      break;
    default:
      break;
  }
  const notify = [PasswordChangeRequired, NewPasswordRequired].some(
    (err) => error instanceof err
  )
    ? notification.info
    : notification.error;
  let shownMessage = message ?? _t`Ошибка авторизации`;
  if (error instanceof Error) {
    shownMessage = error.message;
  }
  notify({ message: shownMessage });
  return error;
};

async function loginUser(
  Username: string,
  Password: string,
  NewPassword?: string,
  data = {}
) {
  return post<
    {
      Action: string;
      User: User;
    },
    unknown,
    unknown
  >('/users/auth/signin', {
    Username,
    Password,
    NewPassword,
    ...data
  });
}

async function refreshToken(Refresh: string) {
  try {
    return await post<
      { Token: string; Refresh: string },
      { Refresh: string },
      unknown
    >('/users/auth/refresh', { Refresh }, { tryOnce: true });
  } catch (e) {
    Sentry.captureException(e, {
      contexts: {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
        response: (e as any)?.response
      }
    });
    throw e;
  }
}
export function UserContext({
  children
}: PropsWithChildren<NonNullable<unknown>>) {
  const { t } = useLingui();
  const [session, dispatch] = useReducer(
    sessionReducer,
    {
      token: localStorage.getItem('token'),
      refresh: localStorage.getItem('refresh'),
      user: null
    },
    (state) => {
      setAuthHeader(state.token);
      return state;
    }
  );
  const { user, token, refresh, complete } = session;

  const logout = useCallback(async () => {
    if (isDemoAccess()) {
      stopDemoAccess();
    } else {
      try {
        await post('/users/auth/signout', null, { tryOnce: true });
      } catch (e) {
        console.warn(e);
      }
    }
    dispatch({ type: 'logout' });
    setAuthHeader(null);
    store.dispatch(api.util.resetApiState());
    history.replace('/');
  }, []);

  useEffect(() => {
    try {
      metrika.userParams(user);
    } catch (e) {
      console.error(e);
    }
  }, [user]);

  useEffect(() => {
    notification.destroy();
    const unbind = Object.keys(SERVICES).map((service) => {
      const axiosInstance = SERVICES[service as keyof typeof SERVICES];
      const interceptor = axiosInstance.interceptors.response.use(
        (r) => r,
        (error) => {
          let status;
          let config: AxiosRequestConfig & { tryOnce: boolean };
          try {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            ({
              response: { status },
              config
            } = error);
          } catch (e) {
            status = undefined;
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            config = error.config;
          }
          if (
            status === 401 &&
            !config.tryOnce &&
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            config.headers?.Authorization &&
            refresh
          ) {
            return refreshToken(refresh)
              .then((data) => {
                dispatch({
                  type: 'updateTokens',
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  data
                });
                setAuthHeader(data.Token);
                localStorage.setItem('token', data.Token);
                localStorage.setItem('refresh', data.Refresh);
                return data.Token;
              })
              .then((newToken) => {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                config.headers.Authorization = newToken;
                if (config.url) {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  return getAxios(config.url)({ ...config, tryOnce: true });
                }
                throw new Error('no url');
              })
              .catch((err) => {
                if (err instanceof Error) {
                  Sentry.captureEvent({
                    message: 'Logout',
                    contexts: {
                      error: {
                        ...err
                      }
                    }
                  });
                }
                return logout();
              });
          }
          return Promise.reject(error);
        }
      );
      return () => axiosInstance.interceptors.response.eject(interceptor);
    });
    return () => unbind.forEach((u) => u());
  }, [token, refresh, logout]);

  const setUser = useCallback((userData: User) => {
    dispatch({ type: 'setUser', data: userData });
    Sentry.setUser({
      username: userData.Username,
      email: userData.Email,
      id: userData.Id
    });
  }, []);

  useEffect(() => {
    if (!user && token) {
      get<User>('/users/profile')
        .then(setUser)
        .catch(() => logout());
    } else {
      dispatch({ type: 'complete' });
    }
  }, [logout, token, user, setUser]);

  const login = useCallback(
    async ({
      login: userLogin,
      password,
      newPassword,
      data: sessionData
    }: LoginData) => {
      if (userLogin === 'demo' && !isDemoAccess()) {
        await startDemoAccess();
      }
      try {
        const data = await loginUser(
          userLogin,
          password,
          newPassword,
          sessionData
        );
        if (data.Action === 'PASSWORD_RESET_REQUIRED') {
          throw new PasswordChangeRequired();
        }
        if (data.Action === 'NEW_PASSWORD_REQUIRED') {
          throw new NewPasswordRequired(data);
        }
        dispatch({ type: 'setSession', data });
      } catch (e) {
        if (e instanceof Error) {
          const err = handleError(e, t`Ошибка входа`);
          if (err) {
            throw err;
          }
        }
      }
    },
    [t]
  );
  const changePassword = useCallback(
    async (
      password: string,
      ConfirmationCode: string,
      Username: string,
      rethrow: unknown
    ) => {
      try {
        await post(
          '/users/auth/forgotpassword',
          {
            Username,
            ConfirmationCode,
            Password: password
          },
          { params: { action: 'confirm' } }
        );
        await login({ login: Username, password });
      } catch (e) {
        if (e instanceof Error) {
          handleError(e, t`Ошибка сброса пароля`);
        }
        if (rethrow) {
          throw e;
        }
      }
    },
    [login, t]
  );

  const isMe = useCallback(
    (id: string) => {
      if (!user) {
        return false;
      }
      return user.Id === id;
    },
    [user]
  );

  const value = useMemo(
    () => ({
      user: user!,
      userId: (user ? user.Id : null)!,
      login,
      logout,
      isMe,
      changePassword
    }),
    [changePassword, isMe, login, logout, user]
  );

  if (!complete) {
    return <Spinner />;
  }

  return <userContext.Provider value={value}>{children}</userContext.Provider>;
}
